import React, { useState, useEffect } from 'react';
import { useTranslation } from '../../../i18n';
import {
  getSoundsEnabled,
  setSoundsEnabled,
  getVolume,
  setVolume,
} from '../../../utils/sounds';
import { AppIcon } from '../icons';

const BAR_HEIGHT = 48;
const BAR_WIDTH_COLLAPSED = BAR_HEIGHT;
const BAR_WIDTH_OPEN = 360;

const ToggleSwitch: React.FC<{
  checked: boolean;
  onChange: () => void;
}> = ({ checked, onChange }) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    onClick={(e) => {
      e.preventDefault();
      e.stopPropagation();
      onChange();
    }}
    style={{
      width: '32px',
      height: '18px',
      borderRadius: '9px',
      border: '1px solid var(--border-medium)',
      background: checked ? 'var(--color-primary)' : 'rgba(248, 250, 252, 0.2)',
      cursor: 'pointer',
      position: 'relative',
      transition: 'background 0.2s',
      flexShrink: 0,
    }}
  >
    <span
      style={{
        position: 'absolute',
        top: '1px',
        left: checked ? '15px' : '1px',
        width: '14px',
        height: '14px',
        borderRadius: '50%',
        background: '#fff',
        transition: 'left 0.2s',
      }}
    />
  </button>
);

const SoundDrawer: React.FC = () => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [sfxOn, setSfxOn] = useState(() => getSoundsEnabled());
  const [volume, setVolumeState] = useState(() => getVolume());

  useEffect(() => {
    if (open) {
      setSfxOn(getSoundsEnabled());
      setVolumeState(getVolume());
    }
  }, [open]);

  const handleSfxToggle = () => {
    const next = !sfxOn;
    setSfxOn(next);
    setSoundsEnabled(next);
  };

  const handleVolumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = parseFloat(e.target.value);
    if (Number.isNaN(v)) return;
    const clamped = Math.max(0, Math.min(1, v));
    setVolumeState(clamped);
    setVolume(clamped);
  };

  return (
    <div
      style={{
        position: 'fixed',
        bottom: '1.25rem',
        left: '1.25rem',
        zIndex: 10001,
        height: `${BAR_HEIGHT}px`,
        width: open ? `${BAR_WIDTH_OPEN}px` : `${BAR_WIDTH_COLLAPSED}px`,
        maxWidth: 'calc(100vw - 2.5rem)',
        borderRadius: '8px',
        background: 'rgba(15, 23, 42, 0.88)',
        backdropFilter: 'blur(12px)',
        border: '1px solid rgba(var(--color-primary-rgb), 0.42)',
        boxShadow: '0 0 0 1px rgba(var(--color-primary-rgb), 0.18), 0 0 18px rgba(var(--color-primary-rgb), 0.28), var(--shadow-md)',
        transition: 'width 0.3s ease-out',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        padding: 0,
        gap: 0,
        boxSizing: 'border-box',
      }}
    >
      <button
        type="button"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setOpen((o) => !o);
        }}
        aria-label={open ? t('common.tooltips.closeSoundSettings') : t('common.tooltips.openSoundSettings')}
        style={{
          width: `${BAR_HEIGHT}px`,
          minWidth: `${BAR_HEIGHT}px`,
          height: '100%',
          border: 'none',
          borderRight: open ? '1px solid var(--border-medium)' : 'none',
          borderRadius: open ? '8px 0 0 8px' : '8px',
          background: 'rgba(var(--color-primary-rgb), 0.08)',
          boxSizing: 'border-box',
          borderLeft: open ? '2px solid var(--color-primary)' : '1px solid rgba(var(--color-primary-rgb), 0.25)',
          color: '#F8FAFC',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: 'inset 0 0 12px rgba(var(--color-primary-rgb), 0.18)',
          transition: 'border 0.2s, background 0.2s, box-shadow 0.2s',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = 'rgba(var(--color-primary-rgb), 0.14)';
          e.currentTarget.style.boxShadow = 'inset 0 0 14px rgba(var(--color-primary-rgb), 0.24), 0 0 12px rgba(var(--color-primary-rgb), 0.2)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = 'rgba(var(--color-primary-rgb), 0.08)';
          e.currentTarget.style.boxShadow = 'inset 0 0 12px rgba(var(--color-primary-rgb), 0.18)';
        }}
      >
        <AppIcon name="volumeUp" fontSize="medium" />
      </button>

      {open && (
        <div
          role="group"
          aria-label={t('lobby.settings.sound')}
          onClick={(e) => e.stopPropagation()}
          onPointerDown={(e) => e.stopPropagation()}
          style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            gap: '0.75rem',
            padding: '0 0.75rem',
            minWidth: 0,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', flexShrink: 0 }}>
            <span style={{ color: 'rgba(248, 250, 252, 0.9)', fontSize: '0.75rem' }}>{t('lobby.settings.sfx')}</span>
            <ToggleSwitch checked={sfxOn} onChange={handleSfxToggle} />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', flex: 1, minWidth: 0 }}>
            <span style={{ color: 'rgba(248, 250, 252, 0.9)', fontSize: '0.75rem', flexShrink: 0 }}>{t('lobby.settings.vol')}</span>
            <input
              type="range"
              min={0}
              max={1}
              step={0.05}
              value={volume}
              onChange={handleVolumeChange}
              onInput={handleVolumeChange}
              onClick={(e) => e.stopPropagation()}
              onPointerDown={(e) => e.stopPropagation()}
              style={{
                flex: 1,
                minWidth: '50px',
                accentColor: 'var(--color-primary)',
                cursor: 'pointer',
              }}
            />
            <span style={{ fontSize: '0.7rem', color: 'rgba(248, 250, 252, 0.6)', flexShrink: 0, minWidth: '2ch' }}>
              {Math.round(volume * 100)}%
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default SoundDrawer;
